<template>
  <AppContainer>
    <form
      autocomplete="off"
      @submit.prevent="submitForm"
    >
      <AppCard>
        <TextInput
          v-model.trim="formData.shortName"
          v-focus
          :validation-error="getValidationError('shortName')"
          label="Short Name"
        />

        <TextInput
          v-model.trim="formData.name"
          :validation-error="hasValidationError('name') ? getValidationError('name') : null"
          label="Name"
        />

        <TextInput
          v-model.trim="formData.sortOrder"
          v-focus
          type="number"
          label="Sort Order"
          :validation-error="hasValidationError('sortOrder') ? getValidationError('sortOrder') : null"
        />

        <ImageFileInput
          class="max-w-md [&_img]:!bg-neutral-400 [&_img]:!p-4 [&_img]:!aspect-square"
          :class="`[&_img]:!bg-[${model?.backgroundColor}]`"
          label="Category Icon"
          :validation-error="getValidationError('icon')"
          :preview="model?.icon"
          :required="!icon && !model?.icon"
          @change="handleIconChange"
        />

        <TextInput
          v-model.trim="formData.backgroundColor"
          label="Background colour"
          :validation-error="hasValidationError('backgroundColor') ? getValidationError('backgroundColor') : null"
        />

        <TextEditorInput
          id="write-up"
          v-model="formData.writeUp"
          label="Category write-up"
          :validation-error="hasValidationError('writeUp') ? getValidationError('writeUp') : null"
          required
        />

        <template #buttons>
          <ButtonSubmitRow
            :cancel-route="{ name: 'app_category_list', params: { framework: route.params.framework } }"
            :processing="isSubmitting"
          />
        </template>
      </AppCard>
    </form>
  </AppContainer>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { AppContainer } from '@/components/app';
import { AppCard } from '@/components/cards';
import { ButtonSubmitRow } from '@/components/buttons';
import { TextEditorInput } from '@/components/form-inputs';

import { useFormSubmit, useHttp } from '@/composables';
import { ImageFileInput, TextInput } from '@/components/form-inputs';

const props = defineProps({
  model: {
    type: Object,
    default: () => null,
  },
});

const { isSubmitting, submit, hasValidationError, getValidationError } = useFormSubmit();
const route = useRoute();
const router = useRouter();

const formData = reactive({
  name: props.model?.name || null,
  backgroundColor: props.model?.backgroundColor || null,
  writeUp: props.model?.writeUp || null,
  shortName: props.model?.shortName || null,
  sortOrder: props.model?.sortOrder || null,
});
const icon = ref(null);
const imagePreview = ref(null);

const submitForm = async () => {
  // Check to ensure the HEX code has a '#' prefixed
  if (formData.backgroundColor) {
    formData.backgroundColor = formData?.backgroundColor.includes('#') ? formData.backgroundColor : ('#' + formData.backgroundColor);
  }

  const categoryResponse = await submit(props.model?.id, formData, null, `frameworks/${route.params.framework}/categories`);

  if (categoryResponse.statusCode === HttpStatusCode.Ok) {
    if (icon.value) {
      isSubmitting.value = true;

      const iconResponse = await uploadIcon(categoryResponse.data.id);

      // If there was an error uploading the icon, prevent the user from being redirected to the framework list page
      if (iconResponse.statusCode !== HttpStatusCode.Ok) {
        isSubmitting.value = false;

        return;
      }
    }

    await router.push({ name: 'app_category_list', params: { framework: route.params.framework } });
  }

  isSubmitting.value = false;
};

const uploadIcon = async categoryId => {
  const data = new FormData();

  data.set('icon', icon.value);

  return await useHttp().post(`/frameworks/${route.params.framework}/categories/${categoryId}/icon`, data);
};

const handleIconChange = e => {
  const file = e.target.files.length ? e.target.files[0] : null;

  if (!file) {
    return;
  }

  icon.value = file;

  // Handles updating the image preview before the form is saved
  const reader = new FileReader();

  reader.onload = event => {
    imagePreview.value = event.target.result;
  };

  reader.readAsDataURL(file);
};
</script>
